import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

export interface ActivityItem {
  id: string;
  title: string;
  timestamp: string;
  borderColor: string; // "#943700", "#004AC6", "#495C95"
}

interface RecentActivityFeedProps {
  items: ActivityItem[];
  onItemClick?: (id: string) => void;
  className?: string;
}

// ============================================
// Component
// ============================================

export function RecentActivityFeed({
  items,
  onItemClick,
  className,
}: RecentActivityFeedProps) {
  const { t } = useI18n();
  return (
    <div className={cn('rounded-xl bg-white p-5 shadow-sm', className)}>
      {/* Title */}
      <h4 className="text-sm font-bold uppercase tracking-[1.4px] text-[#191B23]">
        {t('recentActivity') || 'Recent Activity'}
      </h4>

      {/* Activity Items */}
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onItemClick?.(item.id)}
            className={cn(
              'flex w-full items-start gap-3 text-left',
              'transition-colors hover:bg-slate-50',
              'rounded-lg p-1 -ml-1'
            )}
          >
            {/* Color Border Indicator */}
            <div
              className="mt-0.5 h-8 w-1 flex-shrink-0 rounded-full"
              style={{ backgroundColor: item.borderColor }}
            />

            {/* Content */}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-[#191B23] truncate">
                {item.title}
              </p>
              <p className="mt-0.5 text-[10px] text-[#94A3B8]">
                {item.timestamp}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Empty State */}
      {items.length === 0 && (
        <p className="mt-4 text-center text-xs text-[#94A3B8]">
          {t('noRecentActivity') || 'No recent activity'}
        </p>
      )}
    </div>
  );
}
